import type { SoftHoldStatusResponse } from "@fm/api";

/** Terminal states of a booking request as the renter sees them. */
export type RequestOutcome = "awaiting_approval" | "declined" | "failed";

export type HoldPollResult =
  | { kind: "completed"; rentalId: string; accessToken: string | null }
  | {
      kind: "request";
      outcome: RequestOutcome;
      expiresAt: string | null;
      declineReason: string | null;
      /** Set on a `failed` hold, e.g. "credit_card_required". */
      failureCode: string | null;
      failureReason: string | null;
    }
  | { kind: "continue" };

export const CREDIT_CARD_REQUIRED_CODE = "credit_card_required";

const CREDIT_CARD_REQUIRED_FALLBACK =
  "A credit card is required for the security deposit. Debit and prepaid cards can't be used.";

export function isCreditCardRequiredFailure(
  failureCode: string | null | undefined,
): boolean {
  return failureCode === CREDIT_CARD_REQUIRED_CODE;
}

/** The server's renter-safe reason plus what to do next. */
export function creditCardRequiredMessage(
  failureReason: string | null | undefined,
): string {
  return `${failureReason || CREDIT_CARD_REQUIRED_FALLBACK} Please try again with a credit card.`;
}

/**
 * What one `GET /soft-holds/:id` poll means. Shared by the in-page checkout
 * poll and the 3DS finalize page so both stop on the same states:
 * `awaiting_approval | declined | failed` are terminal for a request, while
 * everything else (`active`, a completed hold whose rental is not linked
 * yet, `cancelled`/`expired`) stays with the caller's existing handling.
 */
export function resolveHoldPoll(
  data: Partial<SoftHoldStatusResponse> | undefined,
): HoldPollResult {
  if (data?.status === "completed" && data.rental_public_id) {
    return {
      kind: "completed",
      rentalId: data.rental_public_id,
      accessToken: data.rental_access_token ?? null,
    };
  }

  if (
    data?.status === "awaiting_approval" ||
    data?.status === "declined" ||
    data?.status === "failed"
  ) {
    return {
      kind: "request",
      outcome: data.status,
      expiresAt: data.expires_at ?? null,
      declineReason: data.decline_reason ?? null,
      failureCode: data.failure_code ?? null,
      failureReason: data.failure_reason ?? null,
    };
  }

  return { kind: "continue" };
}

/**
 * "Oct 5, 2026, 5:00 AM MST". Pass the fleet's IANA zone to show its clock;
 * omitted, the viewer's own zone is used (always labelled, so it is never
 * ambiguous). `null` when there is no usable deadline.
 */
export function formatResponseDeadline(
  expiresAt: string | null | undefined,
  timeZone?: string,
): string | null {
  if (!expiresAt) return null;

  const date = new Date(expiresAt);
  if (Number.isNaN(date.getTime())) return null;

  return (
    new Intl.DateTimeFormat("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
      timeZoneName: "short",
      ...(timeZone ? { timeZone } : {}),
    })
      .format(date)
      // Newer ICU puts a narrow no-break space before AM/PM.
      .replace(/\s/g, " ")
  );
}
